import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { jsPDF } from "jspdf";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { Modal } from "@/components/Modal";
import { Icon } from "@/components/Icon";
import { Switch } from "@/components/Switch";

export type DownloadFormat = "md" | "txt" | "pdf" | "html";

export interface PdfMeta {
    /** Main title shown in the header (e.g. development title) */
    title: string;
    /** Subtitle/code shown in the header (e.g. DEV001) */
    code?: string;
    /** List of label/value pairs shown on the first page as metadata */
    fields?: Array<{ label: string; value: string }>;
}

export interface DownloadAttachmentItem {
    id: number;
    name: string;
}

interface DownloadModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    /** Direct filename (without extension). If omitted, built as YYYY-MM-DD[_prefix][_suffix]. */
    filename?: string;
    prefix?: string;
    suffix?: string;
    content: string;
    formats: DownloadFormat[];
    /** Optional structured metadata for a rich PDF header + meta section */
    pdfMeta?: PdfMeta;
    /** When set and attachments exist, shows a switch to bundle files in a zip. */
    includeFilesOption?: boolean;
    attachments?: DownloadAttachmentItem[];
    loadAttachmentBlob?: (attachmentId: number) => Promise<Blob>;
}

interface FormatConfig {
    labelKey: string;
    extension: string;
    icon: string;
    containerClass: string;
    iconClass: string;
    extensionClass: string;
    nameClass: string;
}

const FORMAT_CONFIGS: Record<DownloadFormat, FormatConfig> = {
    pdf: {
        labelKey: "download_modal.format_pdf",
        extension: ".PDF",
        icon: "pdf",
        containerClass:
            "bg-rose-50/80 hover:bg-rose-100/90 border-rose-200 hover:border-rose-400 hover:shadow-rose-500/10 dark:bg-rose-950/20 dark:hover:bg-rose-950/40 dark:border-rose-900/50",
        iconClass: "text-rose-600 dark:text-rose-400",
        extensionClass:
            "bg-rose-200/70 dark:bg-rose-900/60 text-rose-700 dark:text-rose-300",
        nameClass: "text-rose-950 dark:text-rose-200",
    },
    md: {
        labelKey: "download_modal.format_md",
        extension: ".MD",
        icon: "md",
        containerClass:
            "bg-indigo-50/80 hover:bg-indigo-100/90 border-indigo-200 hover:border-indigo-400 hover:shadow-indigo-500/10 dark:bg-indigo-950/20 dark:hover:bg-indigo-950/40 dark:border-indigo-900/50",
        iconClass: "text-indigo-600 dark:text-indigo-400",
        extensionClass:
            "bg-indigo-200/70 dark:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300",
        nameClass: "text-indigo-950 dark:text-indigo-200",
    },
    txt: {
        labelKey: "download_modal.format_txt",
        extension: ".TXT",
        icon: "txt",
        containerClass:
            "bg-emerald-50/80 hover:bg-emerald-100/90 border-emerald-200 hover:border-emerald-400 hover:shadow-emerald-500/10 dark:bg-emerald-950/20 dark:hover:bg-emerald-950/40 dark:border-emerald-900/50",
        iconClass: "text-emerald-600 dark:text-emerald-400",
        extensionClass:
            "bg-emerald-200/70 dark:bg-emerald-900/60 text-emerald-700 dark:text-emerald-300",
        nameClass: "text-emerald-950 dark:text-emerald-200",
    },
    html: {
        labelKey: "download_modal.format_html",
        extension: ".HTML",
        icon: "html",
        containerClass:
            "bg-amber-50/80 hover:bg-amber-100/90 border-amber-200 hover:border-amber-400 hover:shadow-amber-500/10 dark:bg-amber-950/20 dark:hover:bg-amber-950/40 dark:border-amber-900/50",
        iconClass: "text-amber-600 dark:text-amber-400",
        extensionClass:
            "bg-amber-200/70 dark:bg-amber-900/60 text-amber-700 dark:text-amber-300",
        nameClass: "text-amber-950 dark:text-amber-200",
    },
};

// Primary brand color
const PRIMARY_COLOR: [number, number, number] = [129, 0, 0];
const PRIMARY_DARK: [number, number, number] = [100, 0, 0];
const WHITE: [number, number, number] = [255, 255, 255];
const GRAY_TEXT: [number, number, number] = [60, 60, 60];
const GRAY_LIGHT: [number, number, number] = [240, 240, 240];
const GRAY_BORDER: [number, number, number] = [210, 210, 210];

const HEADER_H = 18;
const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN_X = 14;
const CONTENT_W = PAGE_W - MARGIN_X * 2;
const FOOTER_H = 10;

const buildFilename = (
    filename?: string,
    prefix?: string,
    suffix?: string
): string => {
    if (filename) {
        return filename;
    }

    const datePart = new Date().toISOString().slice(0, 10);
    const parts: string[] = [datePart];

    if (prefix) {
        parts.push(prefix);
    }

    if (suffix) {
        parts.push(suffix);
    }

    return parts.join("_");
};

const buildAttachmentZipName = (
    originalName: string,
    attachmentId: number
): string => {
    const trimmed = originalName.trim() || `attachment-${attachmentId}`;
    const lastDot = trimmed.lastIndexOf(".");

    if (lastDot <= 0) {
        return `${trimmed}-${attachmentId}`;
    }

    const base = trimmed.slice(0, lastDot);
    const extension = trimmed.slice(lastDot);
    return `${base}-${attachmentId}${extension}`;
};

/** Draws the repeating header band on the current page */
const drawHeader = (pdf: jsPDF, meta: PdfMeta): void => {
    // Background band
    pdf.setFillColor(...PRIMARY_COLOR);
    pdf.rect(0, 0, PAGE_W, HEADER_H, "F");

    // Accent stripe at the bottom of the header
    pdf.setFillColor(...PRIMARY_DARK);
    pdf.rect(0, HEADER_H - 1.5, PAGE_W, 1.5, "F");

    // Title (left side)
    pdf.setTextColor(...WHITE);
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "bold");
    const titleTruncated =
        meta.title.length > 60 ? `${meta.title.slice(0, 57)}...` : meta.title;
    pdf.text(titleTruncated, MARGIN_X, 11);

    // Code badge (right side)
    if (meta.code) {
        pdf.setFontSize(7.5);
        pdf.setFont("helvetica", "bold");
        const codeText = meta.code;
        const codeW = pdf.getTextWidth(codeText) + 6;
        const badgeX = PAGE_W - MARGIN_X - codeW;
        pdf.setFillColor(255, 255, 255, 0.15);
        pdf.setDrawColor(...WHITE);
        pdf.setLineWidth(0.3);
        pdf.roundedRect(badgeX, 5, codeW, 7, 1.5, 1.5, "S");
        pdf.text(codeText, badgeX + 3, 10.5);
    }
};

/** Draws the page number footer */
const drawFooter = (pdf: jsPDF, pageNum: number, totalPages: number): void => {
    pdf.setTextColor(180, 180, 180);
    pdf.setFontSize(7);
    pdf.setFont("helvetica", "normal");
    const pageText = `${pageNum} / ${totalPages}`;
    const textW = pdf.getTextWidth(pageText);
    pdf.text(pageText, (PAGE_W - textW) / 2, PAGE_H - 4);
};

const buildPdfDocument = (
    content: string,
    pdfMeta?: PdfMeta,
    modalTitle?: string,
    filename?: string
): jsPDF => {
    const pdf = new jsPDF({ unit: "mm", format: "a4" });
    const meta = pdfMeta ?? { title: modalTitle ?? filename ?? "Document" };

    const contentStartY = HEADER_H + 6;
    const maxY = PAGE_H - FOOTER_H - 4;
    const lineH = 5;
    const smallLineH = 4.5;

    let currentY = contentStartY;
    let pageIndex = 1;

    const ensurePage = (needed: number): void => {
        if (currentY + needed > maxY) {
            pdf.addPage();
            pageIndex++;
            drawHeader(pdf, meta);
            currentY = contentStartY;
        }
    };

    drawHeader(pdf, meta);

    if (meta.fields && meta.fields.length > 0) {
        pdf.setFillColor(...GRAY_LIGHT);
        const metaRowH = smallLineH + 1;
        const metaBlockH = meta.fields.length * metaRowH + 8;
        pdf.roundedRect(MARGIN_X, currentY, CONTENT_W, metaBlockH, 2, 2, "F");

        pdf.setDrawColor(...GRAY_BORDER);
        pdf.setLineWidth(0.3);
        pdf.roundedRect(MARGIN_X, currentY, CONTENT_W, metaBlockH, 2, 2, "S");

        currentY += 4;

        meta.fields.forEach((field) => {
            pdf.setFontSize(7.5);
            pdf.setFont("helvetica", "bold");
            pdf.setTextColor(...PRIMARY_COLOR);
            pdf.text(`${field.label}:`, MARGIN_X + 3, currentY + 3);

            const labelW = pdf.getTextWidth(`${field.label}:`) + 5;

            pdf.setFont("helvetica", "normal");
            pdf.setTextColor(...GRAY_TEXT);
            pdf.text(field.value, MARGIN_X + 3 + labelW, currentY + 3);
            currentY += metaRowH;
        });

        currentY += 4;
    }

    pdf.setDrawColor(...GRAY_BORDER);
    pdf.setLineWidth(0.4);
    pdf.line(MARGIN_X, currentY, PAGE_W - MARGIN_X, currentY);
    currentY += 4;

    pdf.setFontSize(8);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(...GRAY_TEXT);

    const rawLines = content.split("\n");

    rawLines.forEach((rawLine) => {
        const stripped = rawLine.replace(/[^\x20-\x7E\xA0-\xFF]/g, " ").trim();

        const isSectionHeader =
            stripped.startsWith("===") && stripped.endsWith("===");

        if (isSectionHeader) {
            ensurePage(lineH * 2);
            if (currentY > contentStartY + 4) {
                currentY += 2;
            }
            pdf.setFillColor(...PRIMARY_COLOR);
            pdf.rect(MARGIN_X, currentY - 3.5, CONTENT_W, lineH + 1, "F");
            pdf.setTextColor(...WHITE);
            pdf.setFontSize(8);
            pdf.setFont("helvetica", "bold");
            const headerText = stripped
                .replace(/^===\s*/, "")
                .replace(/\s*===$/, "");
            pdf.text(headerText, MARGIN_X + 3, currentY + 1);
            pdf.setFontSize(8);
            pdf.setFont("helvetica", "normal");
            pdf.setTextColor(...GRAY_TEXT);
            currentY += lineH + 2;
            return;
        }

        const isSeparator = /^[-─]{3,}$/.test(stripped);
        if (isSeparator) {
            ensurePage(lineH);
            pdf.setDrawColor(...GRAY_BORDER);
            pdf.setLineWidth(0.3);
            pdf.line(MARGIN_X, currentY, PAGE_W - MARGIN_X, currentY);
            currentY += 3;
            return;
        }

        const wrappedLines = pdf.splitTextToSize(
            stripped || " ",
            CONTENT_W - 4
        );
        wrappedLines.forEach((line: string) => {
            ensurePage(lineH);
            pdf.text(line, MARGIN_X + 2, currentY + 3);
            currentY += lineH;
        });
    });

    const totalPages: number = (
        pdf as unknown as { internal: { getNumberOfPages: () => number } }
    ).internal.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
        pdf.setPage(p);
        drawFooter(pdf, p, totalPages);
    }

    return pdf;
};

const generatePdfBlob = (
    content: string,
    pdfMeta?: PdfMeta,
    modalTitle?: string,
    filename?: string
): Blob => {
    return buildPdfDocument(content, pdfMeta, modalTitle, filename).output(
        "blob"
    );
};

const generatePdf = (
    filename: string,
    content: string,
    pdfMeta?: PdfMeta,
    modalTitle?: string
): void => {
    buildPdfDocument(content, pdfMeta, modalTitle, filename).save(
        `${filename}.pdf`
    );
};

const buildFormatContent = (
    format: DownloadFormat,
    content: string,
    title: string,
    pdfMeta?: PdfMeta
): { body: string | Blob; filename: string; mimeType?: string } => {
    if (format === "pdf") {
        return {
            body: generatePdfBlob(content, pdfMeta, title),
            filename: "chat.pdf",
        };
    }

    if (format === "html") {
        return {
            body: `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>${title}</title></head><body><pre>${content.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;")}</pre></body></html>`,
            filename: "chat.html",
            mimeType: "text/html;charset=utf-8",
        };
    }

    return {
        body: content,
        filename: `chat.${format}`,
        mimeType: "text/plain;charset=utf-8",
    };
};

export const DownloadModal: React.FC<DownloadModalProps> = ({
    isOpen,
    onClose,
    title,
    filename,
    prefix,
    suffix,
    content,
    formats,
    pdfMeta,
    includeFilesOption = false,
    attachments = [],
    loadAttachmentBlob,
}) => {
    const { t } = useTranslation("global");
    const resolvedFilename = buildFilename(filename, prefix, suffix);
    const [includeFiles, setIncludeFiles] = useState<boolean>(false);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const [downloadError, setDownloadError] = useState<string | null>(null);

    const canIncludeFiles =
        includeFilesOption &&
        attachments.length > 0 &&
        Boolean(loadAttachmentBlob);

    useEffect(() => {
        if (!isOpen) {
            setIncludeFiles(false);
            setIsDownloading(false);
            setDownloadError(null);
        }
    }, [isOpen]);

    const downloadBlob = (blob: Blob, extension: string): void => {
        saveAs(blob, `${resolvedFilename}.${extension}`);
    };

    const downloadSingle = (format: DownloadFormat): void => {
        if (format === "pdf") {
            generatePdf(resolvedFilename, content, pdfMeta, title);
            return;
        }

        const value =
            format === "html"
                ? `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>${title}</title></head><body><pre>${content.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;")}</pre></body></html>`
                : content;
        downloadBlob(
            new Blob([value], {
                type:
                    format === "html"
                        ? "text/html;charset=utf-8"
                        : "text/plain;charset=utf-8",
            }),
            format
        );
    };

    const downloadWithFiles = async (format: DownloadFormat): Promise<void> => {
        if (!loadAttachmentBlob) {
            return;
        }

        const zip = new JSZip();
        const chatFile = buildFormatContent(format, content, title, pdfMeta);

        zip.file(chatFile.filename, chatFile.body);

        const filesFolder = zip.folder(t("download_modal.files_folder"));
        const usedNames = new Set<string>();

        for (const attachment of attachments) {
            try {
                const blob = await loadAttachmentBlob(attachment.id);
                if (!blob) {
                    continue;
                }

                let entryName = buildAttachmentZipName(
                    attachment.name,
                    attachment.id
                );

                while (usedNames.has(entryName)) {
                    entryName = buildAttachmentZipName(
                        `${attachment.id}-${entryName}`,
                        attachment.id
                    );
                }

                usedNames.add(entryName);
                filesFolder?.file(entryName, blob);
            } catch (err) {
                console.warn(
                    `[DownloadModal] Could not load attachment ${attachment.id} (${attachment.name}):`,
                    err
                );
            }
        }

        const zipBlob = await zip.generateAsync({ type: "blob" });
        saveAs(zipBlob, `${resolvedFilename}.zip`);
    };

    const download = async (format: DownloadFormat): Promise<void> => {
        setDownloadError(null);
        setIsDownloading(true);

        try {
            if (includeFiles && canIncludeFiles) {
                await downloadWithFiles(format);
            } else {
                downloadSingle(format);
            }
        } catch (err) {
            console.error("[DownloadModal] Error preparing download:", err);
            setDownloadError(t("download_modal.download_error"));
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            cancelLabel={t("modal.close")}
            maxWidth="sm"
        >
            {canIncludeFiles && (
                <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50/80 px-3.5 py-3 dark:border-white/10 dark:bg-dark-subtle/60">
                    <Switch
                        checked={includeFiles}
                        onChange={setIncludeFiles}
                        disabled={isDownloading}
                        label={t("download_modal.include_files_label")}
                        description={t("download_modal.include_files_desc", {
                            count: attachments.length,
                        })}
                        size="sm"
                    />
                </div>
            )}

            {downloadError && (
                <div className="mb-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300">
                    {downloadError}
                </div>
            )}

            {isDownloading && (
                <div className="mb-3 flex items-center justify-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                    <Icon value="loader" size={14} className="animate-spin" />
                    <span>{t("download_modal.preparing_download")}</span>
                </div>
            )}

            <div className="grid grid-cols-2 gap-3.5 pt-1 pb-2">
                {formats.map((format) => {
                    const config = FORMAT_CONFIGS[format];

                    return (
                        <button
                            key={format}
                            type="button"
                            disabled={isDownloading}
                            onClick={() => {
                                void download(format);
                            }}
                            className={`group relative p-4 rounded-2xl border transition-all duration-200 flex flex-col items-center justify-center text-center gap-1.5 cursor-pointer hover:scale-[1.02] active:scale-[0.98] shadow-xs hover:shadow-md disabled:opacity-50 disabled:pointer-events-none ${config.containerClass}`}
                        >
                            <Icon
                                value={config.icon}
                                size={32}
                                className={`${config.iconClass} transition-transform group-hover:scale-110`}
                            />

                            <span
                                className={`text-[11px] font-black tracking-wider uppercase px-2 py-0.5 rounded-md shadow-2xs ${config.extensionClass}`}
                            >
                                {config.extension}
                                {includeFiles && canIncludeFiles
                                    ? t("download_modal.with_files")
                                    : ""}
                            </span>

                            <span
                                className={`text-xs font-bold leading-tight ${config.nameClass}`}
                            >
                                {t(config.labelKey)}
                            </span>
                        </button>
                    );
                })}
            </div>
        </Modal>
    );
};
